import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import * as Yup from "yup";

import TextInput from "../components/TextInput";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { handleError } from "../utils/functions";
import SubmitButton from "../components/SubmitButton";
import { toast } from "react-toastify";
import axiosInstance from "../config/axios.config";
import Select from "./Select";
import { useTranslation } from "react-i18next";

const schema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  shopAddress: Yup.string().required("Shop Address is required"),
  shopName: Yup.string().required("Shop Name is required"),
  email: Yup.string().email("Invalid email format").required("Email is required"),
  shopEmail: Yup.string().email("Invalid email format").required("Shop Email is required"),
  phoneNumber: Yup.string()
    .matches(/^[0-9]+$/, "Phone number must contain only digits")
    .min(10, "Phone number must be at least 10 digits")
    .required("Phone number is required"),
  shopPhoneNumber: Yup.string()
    .matches(/^[0-9]+$/, "Phone number must contain only digits")
    .min(10, "Phone number must be at least 10 digits")
    .required("Phone number is required"),
  password: Yup.string()
    .required("Password is Required")
    .matches(/^(?=.*[a-z])/, "Must Contain One Lowercase Character")
    .matches(/^(?=.*[A-Z])/, "Must Contain One Uppercase Character")
    .matches(/^(?=.*[0-9])/, "Must Contain One Number Character")
    .matches(/^(?=.*[!@#$%^&*])/, "Must Contain One Special Case Character")
    .min(8, "Password must be at least 8 characters long"),
});

const Account = () => {
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const navigate = useNavigate();
  const [country, setCountry] = useState([]);
  const [countryCode, setCountryCode] = useState("");
  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
    setValue,
  } = useForm({ resolver: yupResolver(schema), mode: "all" });
  const { t } = useTranslation();

  useEffect(() => {
    const getData = async () => {
      try {
        const { data } = await axiosInstance.get("/country/active");
        if (!data.error) {
          setCountry(data.data);
          setCountryCode(data.countryCode || "");
        }
      } catch (error) {
        handleError(error);
      }
    };
    getData();
  }, []);

  const onSubmit = async (data) => {
    try {
      if (files.length === 0) {
        toast.error("Please Atleast have at least one document to submit");
        return;
      }

      if (!countryCode) {
        toast.error("Please Select a country");
        return;
      }

      const fd = new FormData();

      for (const key in data) {
        if (Object.prototype.hasOwnProperty.call(data, key)) {
          const element = data[key];
          fd.append(key, element);
        }
      }
      fd.append("countryCode", countryCode);
      Array.from(files).map((item) => fd.append("documents", item));

      const { data: res } = await axiosInstance.post("/seller/signup", fd, {headers:{"Content-Type":"multipart/form-data"}});
      if (!res.error) {
        navigate(`/seller-verify?token=${res.token}`);
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      handleError(error);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    setFiles(droppedFiles);
  };

  const removeFile = (indexToRemove) => {
    setFiles(files.filter((_, index) => index !== indexToRemove));
  };

  return (
    <section className="account py-80">
      <div className="container">
        <form onSubmit={handleSubmit(onSubmit)} autoComplete={false}>
          <div className="row justify-content-center">
            <div className="col-md-4">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex flex-column align-items-end py-20 gap-56">
                    <div className="d-flex align-items-center gap-10">
                      <div className="mainCustomCircle bg-main-two-100 rounded-circle">
                        <h6 className="mb-0 w-60 h-60 bg-main-two-600 text-white d-flex align-items-center justify-content-center rounded-circle border border-5 border-white fw-medium">
                          {" "}
                          01
                        </h6>
                      </div>
                      <div className="">
                        <h6 className="mb-4">{t("personalDetails")}</h6>
                        <p className="text-gray-600">{t("personalDetailsDesc")}</p>
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-10">
                      <div className="mainCustomCircle bg-main-two-100 rounded-circle">
                        <h6 className="mb-0 w-60 h-60 bg-main-two-600 text-white d-flex align-items-center justify-content-center rounded-circle border border-5 border-white fw-medium">
                          {" "}
                          02
                        </h6>
                      </div>
                      <div className="">
                        <h6 className="mb-4">{t("shopDetails")}</h6>
                        <p className="text-gray-600">{t("shopDetailsDesc")}</p>
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-10">
                      <div className="mainCustomCircle bg-main-two-100 rounded-circle">
                        <h6 className="mb-0 w-60 h-60 bg-main-two-600 text-white d-flex align-items-center justify-content-center rounded-circle border border-5 border-white fw-medium">
                          {" "}
                          03
                        </h6>
                      </div>
                      <div className="">
                        <h6 className="mb-4">{t("documents")}</h6>
                        <p className="text-gray-600">{t("documentsDesc")}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <p className="mt-20">
                {t("alreadyHaveAccount")}{" "}
                <a
                  href="https://argetem-seller-rzevk.ondigitalocean.app/"
                  className="text-main-600 text-decoration-underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  {" "}
                  {t("login")}
                </a>
              </p>
            </div>
            {/* Register Card Start */}
            <div className="col-xl-7">
              <div className="card">
                <div className="card-body py-30 px-30">
                  {/* Personal Details Section */}
                  <h6 className="mb-30">{t("personalDetails")}</h6>
                  <div className="mb-24">
                    <TextInput
                      name={"name"}
                      placeholder={"Enter your Full Name"}
                      isRequired
                      label={"Full Name"}
                      type={"text"}
                      register={register}
                      onChange={(e) => setValue("name", e.target.value)}
                      error={errors.name}
                      autoComplete="off"
                    />
                  </div>
                  <div className="mb-24">
                    <TextInput
                      name={"email"}
                      placeholder={"Enter your email address"}
                      isRequired
                      label={"Email Address"}
                      type={"email"}
                      register={register}
                      onChange={(e) => setValue("email", e.target.value)}
                      error={errors.email}
                      autoComplete="off"
                    />
                  </div>
                  <div className="mb-24">
                    <TextInput
                      name={"phoneNumber"}
                      placeholder={"Enter your Phone Number"}
                      isRequired
                      label={"Phone Number"}
                      type={"text"}
                      register={register}
                      onChange={(e) => setValue("phoneNumber", e.target.value)}
                      error={errors.phoneNumber}
                      autoComplete="off"
                    />
                  </div>
                  <div className="mb-24">
                    <TextInput
                      name={"password"}
                      placeholder={"Enter your Pasword"}
                      isRequired
                      label={"Password"}
                      type={"password"}
                      hasicon
                      register={register}
                      error={errors.password}
                      onChange={(e) => setValue("password", e.target.value)}
                    />
                  </div>

                  {/* Shop Details Section */}
                  <h6 className="mb-30 mt-110">{t("shopDetails")}</h6>
                  <div className="mb-24">
                    <TextInput
                      name={"shopName"}
                      placeholder={"Enter your Shop Name"}
                      isRequired
                      label={"Shop Name"}
                      type={"text"}
                      register={register}
                      onChange={(e) => setValue("shopName", e.target.value)}
                      error={errors.shopName}
                    />
                  </div>
                  <div className="mb-24">
                    <TextInput
                      name={"shopEmail"}
                      placeholder={"Enter your Shop email address"}
                      isRequired
                      label={"Shop Email Address"}
                      type={"email"}
                      register={register}
                      onChange={(e) => setValue("shopEmail", e.target.value)}
                      error={errors.shopEmail}
                    />
                  </div>
                  <div className="mb-24">
                    <TextInput
                      name={"shopPhoneNumber"}
                      placeholder={"Enter your Shop Phone Number"}
                      isRequired
                      label={"Shop Phone Number"}
                      type={"text"}
                      register={register}
                      onChange={(e) => setValue("shopPhoneNumber", e.target.value)}
                      error={errors.shopPhoneNumber}
                    />
                  </div>
                  <div className="mb-24">
                    <TextInput
                      name={"shopAddress"}
                      placeholder={"Enter your Shop Adress"}
                      isRequired
                      label={"Shop Adress"}
                      type={"text"}
                      register={register}
                      onChange={(e) => setValue("shopAddress", e.target.value)}
                      error={errors.shopAddress}
                    />
                  </div>
                  <div className="mb-24">
                    <Select
                      placeholder="Select a country"
                      isRequired
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      options={country}
                      label={"Country"}
                    />
                  </div>

                  {/* Documents Section */}
                  <h6 className="mb-30 mt-110">{t("documents")}</h6>
                  <div className="mb-24">
                    <label htmlFor="documentUpload" className="text-neutral-900 text-sm mb-2 fw-medium">
                      {t("uploadDocuments")}
                    </label>
                    
                    <div
                      className={`upload-area ${isDragging ? 'dragging' : ''}`}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      <input
                        type="file"
                        id="documentUpload"
                        className="hidden-input"
                        multiple
                        accept="image/*" // Only allow image files
                        onChange={(e) => {
                          const selectedFiles = Array.from(e.target.files).filter(file => file.type.startsWith('image/'));
                          if (selectedFiles.length !== e.target.files.length) {
                            toast.error("Only image files (JPG, PNG, etc.) are allowed.");
                          }
                          setFiles(selectedFiles);
                        }}
                      />


                      <label htmlFor="documentUpload" className="upload-label">
                        <div className="upload-icon">
                          <i className="fas fa-cloud-upload-alt"></i>
                        </div>
                        <p className="upload-text">{t("dragAndDropFiles")}</p>
                        <span className="browse-text">{t("orBrowseFiles")}</span>
                      </label>
                    </div>

                    {files.length > 0 && (
                      <div className="file-list">
                        {files.map((file, index) => (
                          <div key={index} className="file-item">
                            <div className="file-preview">
                              {file.type.startsWith('image/') ? (
                                <img
                                  src={URL.createObjectURL(file)}
                                  alt={file.name}
                                  className="preview-image"
                                />
                              ) : (
                                <i className="fas fa-file"></i>
                              )}
                            </div>
                            <div className="file-info">
                              <p className="file-name">{file.name}</p>
                              <p className="file-size">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                            </div>
                            <button
                              type="button"
                              className="remove-btn text-danger"
                              onClick={() => removeFile(index)}
                            >
                              X
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Privacy Notice */}
                  <div className="mt-48">
                    <p className="text-gray-500">
                      {t("privacyNotice")}{" "}
                      <Link to="#" className="text-main-600 text-decoration-underline">
                        {t("privacyPolicy")}
                      </Link>
                      .
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-30 text-end">
                <SubmitButton isSubmitting={isSubmitting}>{t("register")}</SubmitButton>
              </div>
            </div>
            {/* Register Card End */}
          </div>
        </form>
      </div>
    </section>
  );
};

export default Account;
